import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import { useBookingScreenEntry } from '../lib/useBookingScreenEntry'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import { useMemberPortalApi } from '../api/memberPortalApiClient'

const EMPTY_CLUB = {
  id: null,
  name: '',
  location: '',
  address: '',
  amenities: [],
  website_url: null,
  logo_url: null,
}

function normalizeAmenities(value) {
  if (!value) return []
  if (Array.isArray(value)) return value.filter(Boolean)
  return []
}

function formatLocation(club) {
  const location = String(club?.location || '').trim()
  if (location) return location
  const address = String(club?.address || '').trim()
  if (address) return address
  return null
}

function formatAddress(club) {
  const address = String(club?.address || '').trim()
  if (address) return address
  return formatLocation(club)
}

function getClubInitials(name) {
  const safeName = String(name || '').trim()
  if (!safeName) return 'RC'
  const parts = safeName.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

export default function MemberReciprocalClubsPage({ clubCode }) {
  const { t: memberText } = useOptionalPrivanaI18n()

  const { memberPortalApiGet, memberPortalApiPost } = useMemberPortalApi()
  const navigate = useNavigate()
  const [clubs, setClubs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState('')
  const [selectedClub, setSelectedClub] = useState(null)
  const requestScreenRef = useRef(null)
  useBookingScreenEntry(selectedClub?.id || 'closed', requestScreenRef)
  const [visitDate, setVisitDate] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [viewMode, setViewMode] = useState('grid')

  const loadClubs = useCallback(async () => {
    if (!clubCode) return
    setLoading(true)
    setError(null)

    try {
      const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/reciprocal-clubs`)
      const json = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(json?.error || 'Unable to load reciprocal clubs.')
      }

      const data = Array.isArray(json?.data) ? json.data : []
      setClubs(data)
    } catch (err) {
      console.error('[members-portal] failed to load reciprocal clubs', err)
      setError("portalPresentation.member_unable_to_load_reciprocal_clubs")
    } finally {
      setLoading(false)
    }
  }, [clubCode, memberPortalApiGet])

  useEffect(() => {
    loadClubs()
  }, [loadClubs])

  useEffect(() => {
    if (!selectedClub) return
    setVisitDate('')
    setNote('')
    setSubmitError('')
  }, [selectedClub])

  const filteredClubs = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    const active = clubs.filter((club) => club?.id)
    if (!normalizedQuery) return active

    return active.filter((club) => {
      const name = String(club?.name || '').toLowerCase()
      const location = String(club?.location || '').toLowerCase()
      const address = String(club?.address || '').toLowerCase()
      return name.includes(normalizedQuery) || location.includes(normalizedQuery) || address.includes(normalizedQuery)
    })
  }, [clubs, query])

  const handleSubmitRequest = useCallback(async () => {
    if (!selectedClub?.id) return
    if (!visitDate) {
      setSubmitError("portalPresentation.member_please_select_a_visit_date_to_request_a_guest_pass")
      return
    }

    setSubmitting(true)
    setSubmitError('')

    try {
      const payload = {
        target_club_id: selectedClub.id,
        visit_date: visitDate,
        note: note.trim() ? note.trim() : null,
      }
      const res = await memberPortalApiPost(
        `/api/members-portal/${clubCode}/reciprocal-access-requests`,
        payload
      )
      const json = await res.json().catch(() => null)

      if (!res.ok) {
        throw new Error(json?.error || 'Unable to submit request.')
      }

      const requestId = json?.data?.id || json?.data?.request_id || json?.request_id || json?.id || null
      navigate(
        `/${clubCode}/reciprocal-clubs/request/success${requestId ? `?request_id=${encodeURIComponent(requestId)}` : ''}`,
        { replace: true }
      )
    } catch (err) {
      console.error('[members-portal] failed to submit reciprocal request', err)
      setSubmitError("portalPresentation.member_unable_to_submit_request")
    } finally {
      setSubmitting(false)
    }
  }, [clubCode, memberPortalApiPost, navigate, note, selectedClub, visitDate])

  return (
    <div className="mp-panel mp-reciprocal-clubs">
      <div className="mp-panel__header mp-spa-services-header">
        <div>
          <p className="mp-eyebrow">{memberText("portalPresentation.member_members_portal")}</p>
          <h4 className="mp-panel__title">{memberText("portalPresentation.member_reciprocal_clubs")}</h4>
          <p className="mp-text-muted">{memberText("portalPresentation.member_browse_partner_clubs_and_request_a_guest_pass_to_visit")}</p>
        </div>
        <div className="mp-spa-services-header__actions">
          <div className="mp-spa-search">
            <input
              type="text"
              placeholder={memberText("portalPresentation.member_search_by_club_or_location")}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <div className="mp-view-toggle">
            <button
              type="button"
              className={`mp-ghost ${viewMode === 'grid' ? 'is-active' : ''}`}
              onClick={() => setViewMode('grid')}
            > {memberText("portalPresentation.member_grid")} </button>
            <button
              type="button"
              className={`mp-ghost ${viewMode === 'list' ? 'is-active' : ''}`}
              onClick={() => setViewMode('list')}
            > {memberText("portalPresentation.member_list")} </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="mp-panel">
          <p className="mp-text-muted">{memberText(error)}</p>
          <button type="button" className="mp-ghost" onClick={loadClubs}> {memberText("portalPresentation.member_retry")} </button>
        </div>
      ) : loading ? (
        <p className="mp-text-muted">{memberText("portalPresentation.member_loading_reciprocal_clubs")}</p>
      ) : filteredClubs.length === 0 ? (
        <div className="mp-empty">{memberText("portalPresentation.member_no_partner_clubs_available_yet")}</div>
      ) : (
        <div className={`mp-golf-course-grid ${viewMode === 'list' ? 'mp-golf-course-grid--list' : ''}`}>
          {filteredClubs.map((club) => {
            const amenities = normalizeAmenities(club?.amenities)
            const websiteUrl = club?.website_url || club?.website || null
            const clubName = club?.name || memberText("portalPresentation.member_reciprocal_club_caption")
            const initials = getClubInitials(clubName)
            const logoUrl = club?.logo_url || null
            return (
              <button
                key={club.id}
                type="button"
                className={`mp-golf-course-card ${viewMode === 'list' ? 'mp-golf-course-card--list' : ''}`}
                onClick={() => setSelectedClub(club)}
              >
                <div className="mp-golf-course-card__content">
                  <div className="mp-logo-thumb">
                    {logoUrl ? (
                      <img src={logoUrl} alt={`${clubName} logo`} />
                    ) : (
                      <span className="mp-logo-thumb__placeholder">{initials}</span>
                    )}
                  </div>
                  <div className="mp-golf-course-card__details">
                    <div className="mp-golf-course-name">{clubName}</div>
                    <div className="mp-text-muted">{(formatLocation(club) || memberText("portalPresentation.member_location_coming_soon"))}</div>
                    {amenities.length ? (
                      <div className="mp-modal__actions" style={{ marginTop: '10px' }}>
                        {amenities.slice(0, 4).map((amenity) => (
                          <span key={amenity} className="mp-pill muted">
                            {amenity}
                          </span>
                        ))}
                        {amenities.length > 4 ? (
                          <span className="mp-pill muted">+{amenities.length - 4} {memberText("portalPresentation.member_more")}</span>
                        ) : null}
                      </div>
                    ) : (
                      <div className="mp-text-muted" style={{ marginTop: '8px' }}> {memberText("portalPresentation.member_amenities_coming_soon")} </div>
                    )}
                    {websiteUrl ? (
                      <a
                        className="mp-link"
                        href={websiteUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        style={{ display: 'inline-flex', marginTop: '8px' }}
                      > {memberText("portalPresentation.member_visit_website")} </a>
                    ) : null}
                  </div>
                </div>
                <div className="mp-golf-course-chip">{memberText("portalPresentation.member_view_details")}</div>
              </button>
            )
          })}
        </div>
      )}

      {selectedClub ? (
        <div className="mp-modal">
          <div className="mp-modal__backdrop" onClick={() => setSelectedClub(null)} />
          <div className="mp-modal__panel mp-accommodation-modal__panel" ref={requestScreenRef}>
            <div className="mp-modal__header">
              <div>
                <p className="mp-eyebrow">{memberText("portalPresentation.member_reciprocal_club")}</p>
                <h4>{selectedClub?.name || EMPTY_CLUB.name}</h4>
              </div>
              <button type="button" className="icon-button" onClick={() => setSelectedClub(null)}>
                <X size={16} />
              </button>
            </div>
            <div className="mp-modal__content mp-accommodation-modal">
              <div className="mp-accommodation-carousel mp-reciprocal-preview">
                {selectedClub?.logo_url ? (
                  <img src={selectedClub.logo_url} alt={`${selectedClub?.name || memberText("portalPresentation.member_club")} logo`} />
                ) : (
                  <div className="mp-reciprocal-preview__placeholder">
                    {getClubInitials(selectedClub?.name || EMPTY_CLUB.name)}
                  </div>
                )}
              </div>
              <div className="mp-reciprocal-modal__details">
                <div className="mp-text-muted">{(formatAddress(selectedClub) || memberText("portalPresentation.member_location_coming_soon"))}</div>
                {normalizeAmenities(selectedClub?.amenities).length ? (
                  <div className="mp-modal__actions">
                    {normalizeAmenities(selectedClub?.amenities).map((amenity) => (
                      <span key={amenity} className="mp-pill muted">
                        {amenity}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="mp-text-muted">{memberText("portalPresentation.member_amenities_coming_soon_2")}</div>
                )}
                {(selectedClub?.website_url || selectedClub?.website) ? (
                  <a
                    className="mp-link"
                    href={selectedClub?.website_url || selectedClub?.website}
                    target="_blank"
                    rel="noreferrer"
                  > {memberText("portalPresentation.member_visit_website")} </a>
                ) : null}

                <div className="mp-accommodation-form">
                  <label>
                    <span>{memberText("portalPresentation.member_visit_date")}</span>
                    <input
                      type="date"
                      value={visitDate}
                      onChange={(event) => setVisitDate(event.target.value)}
                      required
                    />
                  </label>
                </div>

                <label className="mp-accommodation-requests">
                  <span>{memberText("portalPresentation.member_note_message_optional")}</span>
                  <textarea
                    rows={3}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder={memberText("portalPresentation.member_let_us_know_anything_helpful_for_your_visit")}
                  />
                </label>

                {submitError ? <p className="mp-error">{memberText(submitError)}</p> : null}

                <div className="mp-modal__footer mp-accommodation-footer">
                  <div className="mp-accommodation-footer__actions">
                    <button
                      type="button"
                      className="mp-ghost"
                      onClick={() => setSelectedClub(null)}
                      disabled={submitting}
                    > {memberText("portalPresentation.member_cancel")} </button>
                    <button
                      type="button"
                      className="mp-primary"
                      onClick={handleSubmitRequest}
                      disabled={submitting || !visitDate}
                    >
                      {submitting ? memberText("portalPresentation.member_requesting") : memberText("portalPresentation.member_request_guest_pass")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
